"use client";

import { useTranslations } from "@/contexts/I18nContext";
import { APP_NAME, APP_VERSION, LEGAL_NAME, copyrightLine } from "@/lib/constants";

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MARKETING_BASE = "https://zoeyorganizer.com";

export function AboutModal({ isOpen, onClose }: AboutModalProps) {
  const t = useTranslations();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      <div className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md mx-4 p-5 shadow-xl">
        <div className="text-center mb-5">
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{APP_NAME}</p>
          <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">v{APP_VERSION}</p>
        </div>

        <dl className="space-y-3 text-sm">
          <Row label={t("account.about.legalLabel")} value={LEGAL_NAME} />
          <Row label={t("account.about.copyrightLabel")} value={copyrightLine(true)} valueClass="text-xs text-gray-500 dark:text-gray-400 text-right max-w-[60%]" />
        </dl>

        <div className="mt-5 flex flex-col gap-2">
          <a
            href={`${MARKETING_BASE}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-2 rounded-lg text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            {t("account.about.privacy")}
          </a>
          <a
            href={`${MARKETING_BASE}/terms`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-2 rounded-lg text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            {t("account.about.terms")}
          </a>
        </div>

        <button
          onClick={onClose}
          className="mt-5 w-full py-2.5 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          {t("account.about.close")}
        </button>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  labelClass,
  valueClass,
}: {
  label: string;
  value: string;
  labelClass?: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className={labelClass ?? "text-gray-600 dark:text-gray-400"}>{label}</dt>
      <dd className={valueClass ?? "text-gray-900 dark:text-gray-100"}>{value}</dd>
    </div>
  );
}
